import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BASE_PATHS } from '@belong/common';
import { IconForward } from '@belong/icons';
import { Link } from '@belong/ui';
import { DATE_FORMATS, formatDate } from 'utils/formatDate';
import { fetchHomeownerImprovementFixing } from './services';

type Props = {
  section: {
    sectionTitle: string;
    unitId: string;
  };
  setPropertySectionTitleVisibilityByProperty: Dispatch<
    SetStateAction<
      Record<
        string,
        Partial<
          Record<
            | 'upcoming-appointments'
            | 'home-access-instructions'
            | 'home-improvements-tracking',
            boolean
          >
        >
      >
    >
  >;
};

type ImprovementData = {
  estimatedHomeownerFixingCompletionDate: string;
  maintenanceId: string;
  reportId: string;
  status: string;
};

export function HomeImprovementsTracking({
  section,
  setPropertySectionTitleVisibilityByProperty,
}: Props) {
  const dispatch = useDispatch();
  const [showComponent, setShowComponent] = useState(false);
  const [improvementData, setImprovementData] =
    useState<ImprovementData | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const onboardingImprovementStatusResponse =
          await dispatch<ImprovementData>(
            fetchHomeownerImprovementFixing(
              section?.unitId,
            ) as unknown as ImprovementData,
          );

        const {
          estimatedHomeownerFixingCompletionDate,
          maintenanceId,
          reportId,
          status,
        } = onboardingImprovementStatusResponse || {};

        setImprovementData({
          estimatedHomeownerFixingCompletionDate,
          maintenanceId,
          reportId,
          status,
        });

        setShowComponent(!!onboardingImprovementStatusResponse);
      } catch (_error) {
        setShowComponent(false);
      }
    };

    fetchData();
  }, [section?.unitId, dispatch]);

  const address = section?.sectionTitle;

  useEffect(() => {
    if (showComponent) {
      setPropertySectionTitleVisibilityByProperty((prev) => ({
        ...prev,
        [section?.unitId]: {
          ...prev[section?.unitId],
          'home-improvements-tracking': true,
        },
      }));
    }
  }, [
    showComponent,
    section?.unitId,
    setPropertySectionTitleVisibilityByProperty,
  ]);

  if (!showComponent) {
    return null;
  }

  return (
    <div>
      <p className="mb-xs text-h3-sm font-semibold md:mb-2xs md:text-h3-xl">
        Work You&apos;re Tackling
      </p>
      <div className="flex w-full flex-col sm:flex-row md:justify-between md:gap-xl">
        <div className="mb-xl mt-xs flex flex-col items-start justify-between border-0 border-l-3 border-solid border-blue pl-xs sm:mt-sm">
          <p className="mb-2xs">{address}</p>
          <p className="mb-2xs text-h3-sm font-semibold">
            Finishing{' '}
            {formatDate(
              improvementData?.estimatedHomeownerFixingCompletionDate,
              DATE_FORMATS.MONTH_FULLNAME_DAY_SHORT_YEAR_SEPERATED_WITH_COMMA_SPACE,
            )}
          </p>

          <Link
            href={`${BASE_PATHS.ACCOUNTS}/portfolio/maintenance/${improvementData?.maintenanceId}`}
            className="font-semibold"
          >
            VIEW HOME IMPROVEMENT PLAN
            <IconForward height={15} className="ml-xs" />
          </Link>
        </div>
        <div className="mb-xl flex shrink-0 items-center justify-end sm:mt-sm">
          <a
            className="font-semibold"
            href={`${BASE_PATHS.ACCOUNTS}/home/post-move-out/improvements/homeowner-fixes-tracking?home-id=${section?.unitId}`}
          >
            UPDATE OR CONFIRM
          </a>
        </div>
      </div>
    </div>
  );
}
